import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import localUtil from '../../lib/util';
import { getStageTimeLabel } from '../../lib/conventionResults';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

@observer
class StageSelector extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            selectedClass: {},  // keyed by stageMd5
            expandedBlockedGroups: {},  // keyed by group index
        };
    }

    reloadConvention = async () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;

        // Reload convention data to refresh the UI
        await conventionStore.load(convention.id, false, {
            query: {
                hostname: conventionStore.currentHostname,
            },
            addData: ['tags', 'participants'],
        });
    }

    getErrorMessage(errorCode, response) {
        const errorMessages = {
            STAGE_FULL: 'Beklager, runden er nå full. Listen oppdateres.',
            ALREADY_SIGNED_UP: 'Du er allerede påmeldt denne runden.',
            TWO_HOUR_RULE: response?.message || 'For nær en annen runde du er påmeldt.',
            DUPLICATE_CLASS: response?.message || 'Du har allerede brukt denne klassen.',
            MISSING_CLASS: 'Du må velge våpenklasse før påmelding.',
            MISSING_STAGE: 'Ugyldig runde.',
            UNAUTHORIZED: 'Du må være logget inn for å melde deg på.',
        };
        return errorMessages[errorCode] || response?.message || 'Kunne ikke melde på. Prøv igjen.';
    }

    handleSignUp = async (e) => {
        const { stageMd5 } = e.target.closest('button').dataset;
        const { conventionStore, userStore, appState } = this.props.stores;
        const { user = {} } = userStore;
        const { callback } = this.props;
        const { selectedClass } = this.state;
        const shootingClass = selectedClass[stageMd5];

        // Validate weapon class is selected
        if (!shootingClass) {
            appState.addToast({
                type: 'error',
                message: 'Du må velge våpenklasse før påmelding',
            }, 5000);
            return;
        }

        this.setState({ loading: stageMd5 });

        try {
            const response = await conventionStore.newParticipant(user, stageMd5, false, shootingClass);
            console.log('handleSignUp response:', response);

            if (response && (response.status === 202 || response.status === 200)) {
                appState.addToast({
                    type: 'success',
                    message: 'Du er nå påmeldt runden!',
                }, 5000);
                // Reload convention data to update the UI and recalculate blocked stages
                await this.reloadConvention();
                if (callback) {
                    await callback();
                }
            } else if (response && response.errorCode) {
                // Handle specific error codes
                const errorMessage = this.getErrorMessage(response.errorCode, response);
                appState.addToast({
                    type: 'error',
                    message: errorMessage,
                }, 5000);

                // Auto-reload on STAGE_FULL or ALREADY_SIGNED_UP to refresh the UI
                if (response.errorCode === 'STAGE_FULL' || response.errorCode === 'ALREADY_SIGNED_UP') {
                    await this.reloadConvention();
                }
            } else if (response) {
                appState.addToast({
                    type: 'error',
                    message: `Kunne ikke melde på: ${response?.message || 'Ukjent feil'}`,
                }, 5000);
            }
        } catch (err) {
            appState.addToast({
                type: 'error',
                message: `Kunne ikke melde på: ${err.message}`,
            }, 5000);
        } finally {
            this.setState({ loading: false });
        }
    }

    handleResign = async (e) => {
        const dialogAction = beginDialogAction(this, 'handleResign', () => [
            this.props.stores.conventionStore.convention?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { stageMd5 } = e.target.closest('button').dataset;
            const { conventionStore, userStore, appState } = this.props.stores;
            const { user = {} } = userStore;
            const { callback } = this.props;

            // Confirm resignation
            if (!((await appConfirm('Er du sikker på at du vil melde deg av denne runden?')) && dialogAction.isCurrent())) {
                return;
            }

            this.setState({ loading: stageMd5 });

            try {
                const response = await conventionStore.removeParticipantFromStage(user.id, stageMd5);
                console.log('handleResign response:', response);
                if (response && (response.status === 202 || response.status === 200)) {
                    appState.addToast({
                        type: 'success',
                        message: 'Du er nå avmeldt runden.',
                    }, 5000);
                    // Reload convention data to update the UI and recalculate blocked stages
                    await this.reloadConvention();
                    if (callback) {
                        await callback();
                    }
                } else {
                    appState.addToast({
                        type: 'error',
                        message: `Kunne ikke melde av: ${response?.message || 'Ukjent feil'} (${response?.status})`,
                    }, 5000);
                }
            } catch (err) {
                appState.addToast({
                    type: 'error',
                    message: `Kunne ikke melde av: ${err.message}`,
                }, 5000);
            } finally {
                this.setState({ loading: false });
            }
        } finally {
            dialogAction.finish();
        }
    }

    toggleBlockedGroup = (groupIndex) => {
        this.setState(prevState => ({
            expandedBlockedGroups: {
                ...prevState.expandedBlockedGroups,
                [groupIndex]: !prevState.expandedBlockedGroups[groupIndex],
            },
        }));
    }

    // Group stages into: available stages shown individually, blocked stages grouped together
    groupStagesForDisplay(stages) {
        const groups = [];
        let currentBlockedGroup = null;

        stages.forEach((stage, index) => {
            const isBlocked = !stage.canSignUp && stage.blockedReason && !stage.isUserSignedUp;
            const isFull = stage.slotsTotal > 0 && stage.slotsUsed >= stage.slotsTotal;

            if (isBlocked && !isFull) {
                // This is a blocked stage (due to 2-hour rule), add to current blocked group
                if (!currentBlockedGroup) {
                    currentBlockedGroup = {
                        type: 'blocked',
                        stages: [],
                        blockedReason: stage.blockedReason,
                        groupIndex: groups.length,
                    };
                }
                currentBlockedGroup.stages.push(stage);
            } else {
                // This is an available, signed-up, or full stage - show individually
                if (currentBlockedGroup) {
                    // Close the current blocked group
                    groups.push(currentBlockedGroup);
                    currentBlockedGroup = null;
                }
                groups.push({
                    type: 'single',
                    stage,
                });
            }
        });

        // Don't forget the last blocked group
        if (currentBlockedGroup) {
            groups.push(currentBlockedGroup);
        }

        return groups;
    }

    handleClassSelect = (e) => {
        const { stageMd5 } = e.target.dataset;
        const selectedValue = e.target.value;
        this.setState(prevState => ({
            selectedClass: {
                ...prevState.selectedClass,
                [stageMd5]: selectedValue,
            },
        }));
    }

    getStatusBadge(stageInfo) {
        if (stageInfo.isUserSignedUp) {
            return (
                <span class='badge text-bg-success rounded-pill'>
                    <i class='fa-solid fa-check me-1' />
                    <Text id='convention.signed-up-stage'>Signed up</Text>
                </span>
            );
        }
        if (stageInfo.slotsTotal > 0 && stageInfo.slotsUsed >= stageInfo.slotsTotal) {
            return (
                <span class='badge text-bg-danger rounded-pill'>
                    <Text id='convention.stage-full'>Full</Text>
                </span>
            );
        }
        if (!stageInfo.canSignUp && stageInfo.blockedReason) {
            return (
                <span class='badge text-bg-warning rounded-pill'>
                    <i class='fa-solid fa-clock me-1' />
                    {stageInfo.blockedReason}
                </span>
            );
        }
        return (
            <span class='badge text-bg-success rounded-pill'>
                <Text id='convention.stage-available'>Available</Text>
            </span>
        );
    }

    render() {
        const { loading, selectedClass, expandedBlockedGroups } = this.state;
        const { appState, userStore, conventionStore } = this.props.stores;
        const { user = {} } = userStore;
        const { convention } = conventionStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const gapHours = convention.stageGapHours > 0 ? convention.stageGapHours : 2;

        // Check if user is registered for convention
        const isUserRegistered = conventionStore.isUserRegisteredForConvention(user.id);

        // Get stages with availability info (recompute on each render to get fresh data)
        const allStages = conventionStore.getAvailableStagesForUser(user.id);

        // Get user's signed up stages
        const userSignedUpStages = allStages.filter(s => s.isUserSignedUp);

        // Get weapon classes already used by this user
        const usedClasses = conventionStore.getUserUsedClasses(user.id);

        // Copy stages for display
        const displayStages = [...allStages];

        // Sort by date
        displayStages.sort((a, b) => new Date(a.date) - new Date(b.date));

        // Group stages for display (collapse consecutive blocked stages)
        const groupedStages = this.groupStagesForDisplay(displayStages);

        if (!user || !user.id) {
            return (
                <div class='w-100 d-flex justify-content-center'>
                    <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                        <div class={`d-flex flex-column mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='alert alert-danger rounded-lg d-flex flex-column align-items-center'>
                                <i class='fa-duotone fa-right-to-bracket mb-2' style='font-size: 3.0em;' />
                                <Text id='convention.must-login-stage-selector'>You must login to select a stage.</Text>
                            </div>
                            <a class='btn btn-primary btn-lg btn-block rounded-pill mt-2' href='/login'>
                                <i class='fa-duotone fa-right-to-bracket me-2' />
                                <Text id='convention.log-in'>Log in</Text>
                            </a>
                        </div>
                    </div>
                </div>
            );
        }

        if (!isUserRegistered) {
            return (
                <div class='w-100 d-flex justify-content-center'>
                    <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                        <div class={`d-flex flex-column mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='alert alert-warning rounded-lg d-flex flex-column align-items-center'>
                                <i class='fa-duotone fa-user-plus mb-2' style='font-size: 3.0em;' />
                                <Text id='convention.must-register-first'>You must register for the convention before you can choose stages.</Text>
                            </div>
                            <div class='text-center text-body-secondary'>
                                <Text id='convention.register-first-instructions'>Please sign up for the convention first using the signup page.</Text>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (!convention.selfSignup) {
            return (
                <div class='w-100 d-flex justify-content-center'>
                    <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                        <div class={`d-flex flex-column mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='alert alert-info rounded-lg d-flex flex-column align-items-center'>
                                <i class='fa-duotone fa-info-circle mb-2' style='font-size: 3.0em;' />
                                <Text id='convention.self-signup-disabled'>Self signup is not enabled for this convention.</Text>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class={`font-weight-lighter px-3 mt-3 box-header w-100 position-relative`}>
                        {convention.title}
                    </div>

                    {/* User's current stages */}
                    {userSignedUpStages.length > 0 && (
                        <div class={`d-flex flex-column mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='d-flex flex-row align-items-center mb-2'>
                                <i class='fa-duotone fa-check-circle text-success me-2' style='font-size: 1.5em;' />
                                <span class='font-weight-bold'>
                                    <Text id='convention.your-stages'>Your stages</Text>
                                </span>
                                <span class='badge text-bg-secondary ms-2'>{userSignedUpStages.length}</span>
                            </div>
                            <div class='d-flex flex-wrap gap-2'>
                                {userSignedUpStages.map(stage => {
                                    const isLoading = loading === stage.md5;
                                    const isAnyLoading = !!loading;
                                    return (
                                        <div
                                            key={stage.md5}
                                            class='d-flex align-items-center badge text-bg-success rounded-pill px-2 py-2'
                                            style='font-size: 0.95em;'
                                        >
                                            <i class='fa-solid fa-check me-1' />
                                            {stage.name} - {getStageTimeLabel(stage, convention)}
                                            {stage.userClass && <span class='ms-1'>({stage.userClass})</span>}
                                            <button
                                                class='btn btn-sm btn-light rounded-circle ms-2 p-0 d-flex align-items-center justify-content-center'
                                                style='width: 22px; height: 22px; min-width: 22px;'
                                                onClick={this.handleResign}
                                                data-stage-md5={stage.md5}
                                                disabled={isAnyLoading}
                                                title='Meld av'
                                            >
                                                {isLoading ? (
                                                    <i class='fa-duotone fa-spinner-third fa-spin' style='font-size: 0.7em;' />
                                                ) : (
                                                    <i class='fa-solid fa-xmark text-danger' style='font-size: 0.8em;' />
                                                )}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Stage list */}
                    <div class={`font-weight-lighter px-3 mt-3 box-header w-100 position-relative`}>
                        <Text id='convention.available-stages'>Available stages</Text>
                        <span class='badge text-bg-secondary ms-2'>{displayStages.length}</span>
                    </div>

                    {displayStages.length === 0 ? (
                        <div class={`d-flex flex-column mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='text-center text-body-secondary py-4'>
                                <i class='fa-duotone fa-calendar-xmark mb-2' style='font-size: 2em;' />
                                <br />
                                <Text id='convention.no-stages-available'>No stages available</Text>
                            </div>
                        </div>
                    ) : (
                        <div class={`d-flex flex-column mt-3 mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            {groupedStages.map((group, groupIdx) => {
                                if (group.type === 'blocked') {
                                    // Collapsed group of blocked stages
                                    const isExpanded = expandedBlockedGroups[group.groupIndex];
                                    const firstStage = group.stages[0];
                                    const lastStage = group.stages[group.stages.length - 1];
                                    const stageCount = group.stages.length;

                                    return (
                                        <div key={`blocked-${group.groupIndex}`} class={groupIdx > 0 ? 'border-top' : ''}>
                                            {/* Collapsed header */}
                                            <div
                                                class='px-3 py-2 d-flex justify-content-between align-items-center'
                                                style='cursor: pointer;'
                                                onClick={() => this.toggleBlockedGroup(group.groupIndex)}
                                            >
                                                <div class='d-flex align-items-center'>
                                                    <i class={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'} me-2 text-body-secondary`} style='width: 12px;' />
                                                    <span class='text-body-secondary'>
                                                        {stageCount === 1 ? (
                                                            firstStage.name
                                                        ) : (
                                                            <>{firstStage.name} ... {lastStage.name}</>
                                                        )}
                                                        <span class='ms-2 small'>({stageCount} lag)</span>
                                                    </span>
                                                </div>
                                                <span class='badge text-bg-warning rounded-pill small'>
                                                    <i class='fa-solid fa-clock me-1' />
                                                    {group.blockedReason}
                                                </span>
                                            </div>

                                            {/* Expanded content */}
                                            {isExpanded && group.stages.map((stageInfo, stageIdx) => {
                                                const isLoading = loading === stageInfo.md5;
                                                const isAnyLoading = !!loading;
                                                const isDisabled = !stageInfo.canSignUp || stageInfo.isUserSignedUp || isAnyLoading;
                                                const slotsAvailable = stageInfo.slotsTotal > 0
                                                    ? stageInfo.slotsTotal - stageInfo.slotsUsed
                                                    : null;

                                                return (
                                                    <div
                                                        key={stageInfo.md5}
                                                        class='px-3 py-2 border-top'
                                                        style='margin-left: 20px;'
                                                    >
                                                        <div class='d-flex justify-content-between align-items-center mb-1'>
                                                            <span class='fw-bold'>{stageInfo.name}</span>
                                                            {this.getStatusBadge(stageInfo)}
                                                        </div>
                                                        <div class='text-body-secondary small'>
                                                            {util.formatDate(stageInfo.date, {
                                                                hour12: false,
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                weekday: 'short',
                                                                day: 'numeric',
                                                                month: 'short',
                                                            })}
                                                            {slotsAvailable !== null && (
                                                                <>
                                                                    <span class='mx-2'>•</span>
                                                                    {slotsAvailable} <Text id='convention.slots-text'>plasser</Text>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                }

                                // Single stage (available, signed-up, or full)
                                const stageInfo = group.stage;
                                const isLoading = loading === stageInfo.md5;
                                const isAnyLoading = !!loading;
                                const isDisabled = !stageInfo.canSignUp || stageInfo.isUserSignedUp || isAnyLoading;
                                const slotsAvailable = stageInfo.slotsTotal > 0
                                    ? stageInfo.slotsTotal - stageInfo.slotsUsed
                                    : null;

                                return (
                                    <div
                                        key={stageInfo.md5}
                                        class={`px-3 py-3 ${groupIdx > 0 ? 'border-top' : ''}`}
                                    >
                                        {/* Row 1: Name + Badge */}
                                        <div class='d-flex justify-content-between align-items-center mb-1'>
                                            <span class='fw-bold'>{stageInfo.name}</span>
                                            {this.getStatusBadge(stageInfo)}
                                        </div>

                                        {/* Row 2: Date + Slots (compact) */}
                                        <div class='text-body-secondary small mb-2'>
                                            {util.formatDate(stageInfo.date, {
                                                hour12: false,
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                weekday: 'short',
                                                day: 'numeric',
                                                month: 'short',
                                            })}
                                            {slotsAvailable !== null && (
                                                <>
                                                    <span class='mx-2'>•</span>
                                                    {slotsAvailable} <Text id='convention.slots-text'>plasser</Text>
                                                </>
                                            )}
                                        </div>

                                        {/* Row 3: Actions */}
                                        {stageInfo.isUserSignedUp ? (
                                            <div class='d-flex gap-2 align-items-center'>
                                                {stageInfo.userClass && (
                                                    <span class='badge text-bg-secondary rounded-pill'>
                                                        {stageInfo.userClass}
                                                    </span>
                                                )}
                                                <button
                                                    class='btn btn-sm btn-outline-danger'
                                                    onClick={this.handleResign}
                                                    data-stage-md5={stageInfo.md5}
                                                    disabled={isAnyLoading}
                                                >
                                                    {isLoading ? (
                                                        <i class='fa-duotone fa-spinner-third fa-spin' />
                                                    ) : (
                                                        <>
                                                            <i class='fa-solid fa-xmark me-1' />
                                                            <Text id='convention.resign-stage'>Resign</Text>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        ) : (
                                            <div class='d-flex gap-2'>
                                                <select
                                                    class='form-select form-select-sm'
                                                    style='flex: 0 0 50%; max-width: 160px;'
                                                    value={selectedClass[stageInfo.md5] || ''}
                                                    data-stage-md5={stageInfo.md5}
                                                    onChange={this.handleClassSelect}
                                                    disabled={isDisabled}
                                                >
                                                    <option value=''>Velg klasse</option>
                                                    <option value='fin' disabled={usedClasses.includes('fin')}>Fin {usedClasses.includes('fin') ? '(brukt)' : ''}</option>
                                                    <option value='grov' disabled={usedClasses.includes('grov')}>Grov {usedClasses.includes('grov') ? '(brukt)' : ''}</option>
                                                    <option value='mil' disabled={usedClasses.includes('mil')}>Militær {usedClasses.includes('mil') ? '(brukt)' : ''}</option>
                                                    <option value='milrp' disabled={usedClasses.includes('milrp')}>Militær RP {usedClasses.includes('milrp') ? '(brukt)' : ''}</option>
                                                    <option value='rev' disabled={usedClasses.includes('rev')}>Revolver {usedClasses.includes('rev') ? '(brukt)' : ''}</option>
                                                    <option value='revrp' disabled={usedClasses.includes('revrp')}>Revolver RP {usedClasses.includes('revrp') ? '(brukt)' : ''}</option>
                                                    <option value='sp.rev' disabled={usedClasses.includes('sp.rev')}>Sp.rev {usedClasses.includes('sp.rev') ? '(brukt)' : ''}</option>
                                                    <option value='sp.pistol' disabled={usedClasses.includes('sp.pistol')}>Sp.pistol {usedClasses.includes('sp.pistol') ? '(brukt)' : ''}</option>
                                                    <option value='mag1' disabled={usedClasses.includes('mag1')}>Magnum 1 {usedClasses.includes('mag1') ? '(brukt)' : ''}</option>
                                                    <option value='mag2' disabled={usedClasses.includes('mag2')}>Magnum 2 {usedClasses.includes('mag2') ? '(brukt)' : ''}</option>
                                                    <option value='luft' disabled={usedClasses.includes('luft')}>Luft {usedClasses.includes('luft') ? '(brukt)' : ''}</option>
                                                </select>
                                                <button
                                                    class={`btn btn-sm ${isDisabled || !selectedClass[stageInfo.md5] ? 'btn-outline-secondary' : 'btn-primary'}`}
                                                    onClick={this.handleSignUp}
                                                    data-stage-md5={stageInfo.md5}
                                                    disabled={isDisabled || !selectedClass[stageInfo.md5]}
                                                >
                                                    {isLoading ? (
                                                        <i class='fa-duotone fa-spinner-third fa-spin' />
                                                    ) : (
                                                        <>
                                                            <i class='fa-solid fa-plus me-1' />
                                                            <Text id='convention.sign-up-stage'>Meld på</Text>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Info about 2-hour rule */}
                    <div class={`d-flex flex-column mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} border border-info`}>
                        <div class='d-flex flex-row align-items-start'>
                            <i class='fa-duotone fa-info-circle text-info me-2 mt-1' />
                            <div class='d-flex flex-column'>
                                <span class='font-weight-bold text-info'>
                                    <Text id='convention.stage-gap-rule-title' fields={{ hours: gapHours }}>
                                        {gapHours}-hour rule
                                    </Text>
                                </span>
                                <small class='text-body-secondary'>
                                    <Text id='convention.stage-gap-rule-description' fields={{ hours: gapHours }}>
                                        You must have at least {gapHours} hours between each stage you sign up for.
                                    </Text>
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default StageSelector;
